import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import ReactTooltip, { TooltipProps } from 'react-tooltip';
import { nanoid } from '@reduxjs/toolkit';

import styles from './IconTooltip.module.scss';

interface IProps {
  IconComponent: any;
  id: string;
  content: string;
  // react-tooltip already flips a placement that would not fit the viewport, so
  // this is a preference for where the tooltip opens first, not a clipping fix.
  place?: TooltipProps['place'];
}

const IconTooltip = ({
  id,
  content,
  IconComponent,
  place = 'right',
}: IProps) => {
  // react-tooltip resolves a target by id alone, and callers pass a name shared
  // by every instance of their section — one per patient row. Suffixing it lets
  // each icon own the tooltip it opens.
  const tooltipId = useMemo(() => `${id}-${nanoid()}`, [id]);

  return (
    <div>
      <div data-for={tooltipId} data-tip="">
        <IconComponent />
      </div>

      {/*
        The tooltip is positioned `fixed`, but an icon rendered inside a
        Material UI popper sits under a `transform`, which makes that popper the
        containing block — the tooltip would then be measured against the popper
        and clipped by its scrolling paper. Rendering it on `body` keeps it
        anchored to the viewport wherever the icon lives.
      */}
      {createPortal(
        <ReactTooltip
          id={tooltipId}
          place={place}
          effect="solid"
          className={`${styles.tooltip} shadow-tooltip`}
        >
          <div className={styles.content}>{content}</div>
        </ReactTooltip>,
        document.body
      )}
    </div>
  );
};

export default IconTooltip;
